import { useRef } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/navigation';
import key from 'weak-key';
import { Footer, Icons } from '@geberit/gdds';

// styles
import styles from './gddsFooter.module.scss';

// types
import type { GddsFooterProps } from './footer.types';

// components
import { EditWrapper } from '../ContentCreator/EditWrapper';
import { SearchIndexOff } from '../SearchIndex/SearchIndexOff';
import { TPP_TYPE_TECHNICAL } from '../ContentCreator/tppConstants';

// utils
import {
  footerCountrySwitchSelector,
  usercentricsSettingsId,
  globalsServerSelector,
} from 'utils/selectors/globalsSelectors';
import { useTracking } from 'utils/hooks/useTracking';
import { useNord } from 'utils/hooks/use-nord';
import { useXy } from 'utils/hooks/use-xy';
import { gridSizes, buildSize } from 'utils/gridSize';
import { handlePopup } from 'utils/openInPopup';
import { useTranslationFunction } from 'utils/hooks/use-translations';
import { classNameBuilder } from 'utils/classNameBuilder';
import { useUsercentricsWorkaround } from 'features/cookie-consent/hooks/use-usercentrics-workaround';
import { footerTrackingAction } from './trackingActions';
import { useIsLeadManagementLanding } from 'utils/hooks/use-is-lead-management-landing';

function GddsFooter({
  toTopButton,
  socialMediaItems,
  previewId,
  navigationItems,
}: Readonly<GddsFooterProps>) {
  const track = useTracking();
  const footerRef = useRef<HTMLDivElement | null>(null);
  const router = useRouter();
  const isNordics = useNord();
  const isXy = useXy();
  const translate = useTranslationFunction();
  const uc_settingsId = useSelector(usercentricsSettingsId);
  const countrySwitch = useSelector(footerCountrySwitchSelector);
  const { setUcOpen } = useUsercentricsWorkaround();
  const server = useSelector(globalsServerSelector);

  const isLeadManagementLanding = useIsLeadManagementLanding(server);

  const onClickNaviItem = (naviItem: GddsFooterProps['navigationItems'][number]) => {
    if (naviItem.url?.includes('http://') || naviItem.url?.includes('https://')) {
      if (naviItem.window === '_popup') {
        const windowId = key(naviItem);
        handlePopup({ target: naviItem.url }, windowId);
      } else {
        window.open(naviItem.url, naviItem.window);
      }
    } else if (naviItem.type !== 'cookie_disclaimer') {
      router.push(naviItem.url);
    } else if (uc_settingsId) {
      window.UC_UI.showFirstLayer();
      setUcOpen(true);
    }

    track.trackEvent(footerTrackingAction('Navigation', naviItem.label));
  };

  const onClickSocial = (name: string, target: string) => {
    track.trackEvent(footerTrackingAction('SocialNavigation', name));
    window.open(target);
  };

  return (
    <SearchIndexOff>
      <EditWrapper previewId={previewId} editType={TPP_TYPE_TECHNICAL}>
        <div className={styles.footer} ref={footerRef}>
          <Footer
            maxContentWidth={buildSize(isNordics || isXy ? gridSizes.gddsFullGrid : gridSizes.full)}
            copyright={{ text: translate('web20_page_copy') }}
            regionSelect={
              isLeadManagementLanding
                ? {}
                : {
                    title:
                      isNordics || isXy ? translate('web20_footer_country_selector_label') : '',
                    region: isNordics || isXy ? (countrySwitch?.countryTranslation ?? '') : '',
                    target: isXy ? (countrySwitch?.pageref ?? '') : '',
                    windowTarget: isXy ? '_self' : '',
                  }
            }
            socialNavigation={
              isLeadManagementLanding
                ? {}
                : {
                    items:
                      socialMediaItems
                        ?.filter((item) => item.url)
                        .map((item) => {
                          // TODO: quick fix for backend LanguageSpecificGlobals['socialMedia'] type
                          const icon = item.type === 'twitter' ? 'x' : item.type;
                          return {
                            color: '#000',
                            symbol: Icons[icon] ? icon : 'help',
                            onClick: () => onClickSocial(icon, item.url),
                          };
                        }) || [],
                  }
            }
            metaNavigation={{
              items:
                navigationItems?.map((item) => ({
                  title: item.label,
                  onClick: () => onClickNaviItem(item),
                  className:
                    item.type === 'cookie_disclaimer'
                      ? classNameBuilder('footer-cookie-link', !uc_settingsId && 'ucShow_e7Q5KaN7e')
                      : undefined,
                })) || [],
            }}
            toTopButton={toTopButton}
          />
        </div>
      </EditWrapper>
    </SearchIndexOff>
  );
}

export default GddsFooter;
